import { useState } from 'react';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/TextNew';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';

import { userStore } from 'stores/global';

import {
  useAssetLibraryCompactLayout,
  useAssetLibraryEmbedConfig,
} from '../assetLibraryEmbedContext';
import { isDisplayableAssetCreator } from '../assetLibraryFilters';
import type { AssetLibraryViewMode } from '../assetLibraryGridLayout';
import { useStore } from '../assetLibraryStore';
import {
  type AssetLibraryDisplayItem,
  formatAssetUpdatedLabel,
  getAssetCardMetadata,
  getAssetCardTitle,
  isCardBookmarkVisible,
  isTeamPlan,
  shouldShowStackedPreview,
} from '../utils';
import { getAssetLibraryCardLayoutClasses } from './AssetLibraryCardShell';
import AssetLibraryCardShell from './AssetLibraryCardShell';
import AssetLibraryCardThumbnailOverlay from './AssetLibraryCardThumbnailOverlay';
import AssetLibraryCardThumbnailShell from './AssetLibraryCardThumbnailShell';
import AssetLibraryCreatorAvatar from './AssetLibraryCreatorAvatar';
import AssetLibraryStackedThumbnail from './AssetLibraryStackedThumbnail';

const STACK_PEEK_WIDTH_PX = 16;

export interface AssetLibraryCardProps {
  asset: AssetLibraryDisplayItem;
  layout?: AssetLibraryViewMode;
  bulkSelectionEnabled?: boolean;
  onClick?: () => void;
  onToggleSelection?: () => void;
  selected?: boolean;
}

const CREATOR_AVATAR_SIZE = 5;
const CREATOR_AVATAR_INDENT_CLASS = 'pl-7';

export default function AssetLibraryCard({
  asset,
  layout = 'grid',
  bulkSelectionEnabled = false,
  onClick,
  onToggleSelection,
  selected = false,
}: AssetLibraryCardProps) {
  const store = useStore();
  const { organizationInfo, organizationMembers } = useSnapshot(
    userStore.state,
  );
  const embedConfig = useAssetLibraryEmbedConfig();
  const compact = useAssetLibraryCompactLayout();
  const autoPlayVideo = embedConfig?.autoPlayThumbnails ?? false;
  const { titleToken } = getAssetLibraryCardLayoutClasses(compact);
  const [isHovered, setIsHovered] = useState(false);
  const metadata = getAssetCardMetadata(asset);
  const showStackedPreview = shouldShowStackedPreview(asset);
  const title = getAssetCardTitle(asset);
  const { libraryView } = store.state;
  const isTrashView = libraryView === 'trash';
  const isInteractive = Boolean(onClick) && !isTrashView;
  const isListLayout = layout === 'list';
  const showBookmark = isCardBookmarkVisible(libraryView);
  const showCreatorAvatar = isTeamPlan(organizationInfo?.planType);
  const metadataIndentClass = showCreatorAvatar
    ? CREATOR_AVATAR_INDENT_CLASS
    : undefined;
  const modalSelectionOverlay =
    !bulkSelectionEnabled && selected ? (
      <div className="bg-blue absolute left-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded">
        <CheckmarkIcon className="h-3 w-3 text-white" />
      </div>
    ) : null;

  return (
    <AssetLibraryCardShell
      compact={compact}
      layout={layout}
      interactive={isInteractive}
      selected={selected}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`relative shrink-0 ${
          isListLayout ? 'aspect-video w-[120px]' : 'aspect-video w-full'
        }`}
        style={
          showStackedPreview ? { paddingRight: STACK_PEEK_WIDTH_PX } : undefined
        }
      >
        {showStackedPreview ? (
          <div className="relative h-full w-full">
            <AssetLibraryStackedThumbnail
              asset={asset}
              isHovered={isHovered}
              autoPlayVideo={autoPlayVideo}
            />
            <div className="z-60 absolute inset-0">
              <AssetLibraryCardThumbnailOverlay
                asset={asset}
                embedded={Boolean(embedConfig)}
                isHovered={isHovered}
                bulkSelectionEnabled={bulkSelectionEnabled}
                showBookmark={showBookmark}
                selected={selected}
                onClick={onClick}
                onToggleSelection={onToggleSelection}
              />
            </div>
          </div>
        ) : (
          <AssetLibraryCardThumbnailShell
            asset={asset}
            embedded={Boolean(embedConfig)}
            isHovered={isHovered}
            autoPlayVideo={autoPlayVideo}
            onClick={onClick}
            bulkSelectionEnabled={bulkSelectionEnabled}
            showBookmark={showBookmark}
            selected={selected}
            onToggleSelection={onToggleSelection}
            overlay={modalSelectionOverlay}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-1 px-2">
        <div className="flex min-w-0 items-center gap-2">
          {showCreatorAvatar ? (
            isDisplayableAssetCreator(asset.createdByUser) ? (
              <AssetLibraryCreatorAvatar
                size={CREATOR_AVATAR_SIZE}
                className="shrink-0"
                userID={asset.createdByUser}
                organizationMembers={organizationMembers}
              />
            ) : (
              <div
                className="bg-gray-30 flex shrink-0 items-center justify-center rounded-full"
                style={{
                  width: `${CREATOR_AVATAR_SIZE / 4}rem`,
                  height: `${CREATOR_AVATAR_SIZE / 4}rem`,
                }}
                aria-label="Unknown creator"
              >
                <Text token="14/medium" className="text-secondary leading-none">
                  ?
                </Text>
              </div>
            )
          ) : null}
          <Text
            token={titleToken}
            className="text-secondary min-w-0 truncate"
            title={title}
          >
            {title}
          </Text>
        </div>
        <Text
          token={compact ? { base: '12', sm: '14' } : '14'}
          className={`text-tertiary truncate ${metadataIndentClass ?? ''}`}
          title={metadata}
        >
          {metadata}
        </Text>
        <Text
          token={compact ? { base: '12', sm: '14' } : '14'}
          className={`text-tertiary ${metadataIndentClass ?? ''}`}
        >
          {formatAssetUpdatedLabel(asset.lastUpdatedAt)}
        </Text>
      </div>
    </AssetLibraryCardShell>
  );
}
